/**
 * Hierarchical Structure Type System for Virtual Staging
 * 
 * Provides a complete taxonomy of real estate interior and exterior spaces
 * with support for progressive drill-down selection.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface StructureType {
  id: string;
  label: string;
  description?: string;
  suggestedStyles?: string[];
}

export interface StructureCategory {
  id: string;
  label: string;
  icon?: string;
  types: StructureType[];
}

export interface StructureDomain {
  id: 'interior' | 'exterior';
  label: string;
  icon?: string;
  categories: StructureCategory[];
}

export type StructureHierarchy = StructureDomain[];

export interface StructureSelection {
  domain: 'interior' | 'exterior';
  categoryId: string;
  typeId: string;
  // Computed for display convenience
  domainLabel: string;
  categoryLabel: string;
  typeLabel: string;
  fullPath: string;
}

// ============================================================================
// Complete Hierarchy Definition
// ============================================================================

export const structureHierarchy: StructureHierarchy = [
  {
    id: 'exterior',
    label: 'Exterior',
    icon: 'Home',
    categories: [
      {
        id: 'facade',
        label: 'Building Facade',
        icon: 'Building',
        types: [
          { id: 'sfh_front', label: 'Single Family Home - Front', suggestedStyles: ['modern', 'traditional', 'farmhouse', 'contemporary'] },
          { id: 'sfh_rear', label: 'Single Family Home - Rear' },
          { id: 'townhouse', label: 'Townhouse / Rowhouse' },
          { id: 'condo_building', label: 'Condo / Apartment Building' },
          { id: 'multifamily', label: 'Multi-Family Property' },
        ]
      },
      {
        id: 'outdoor_spaces',
        label: 'Outdoor Spaces',
        icon: 'Trees',
        types: [
          { id: 'backyard', label: 'Backyard' },
          { id: 'front_yard', label: 'Front Yard / Lawn' },
          { id: 'side_yard', label: 'Side Yard' },
          { id: 'courtyard', label: 'Courtyard' },
          { id: 'rooftop', label: 'Rooftop / Terrace' },
        ]
      },
      {
        id: 'entry_access',
        label: 'Entry & Access',
        icon: 'DoorOpen',
        types: [
          { id: 'front_porch', label: 'Front Porch / Entryway' },
          { id: 'back_porch', label: 'Back Porch' },
          { id: 'stoop', label: 'Stoop / Steps' },
          { id: 'breezeway', label: 'Breezeway' },
          { id: 'porte_cochere', label: 'Porte Cochère' },
        ]
      },
      {
        id: 'outdoor_living',
        label: 'Outdoor Living',
        icon: 'Umbrella',
        types: [
          { id: 'deck', label: 'Deck' },
          { id: 'patio', label: 'Patio' },
          { id: 'pergola', label: 'Pergola / Gazebo' },
          { id: 'outdoor_kitchen', label: 'Outdoor Kitchen' },
          { id: 'fire_pit_area', label: 'Fire Pit Area' },
        ]
      },
      {
        id: 'parking_garage',
        label: 'Parking & Garage',
        icon: 'Car',
        types: [
          { id: 'driveway', label: 'Driveway' },
          { id: 'garage_exterior', label: 'Garage Exterior' },
          { id: 'carport', label: 'Carport' },
          { id: 'parking_pad', label: 'Parking Pad' },
        ]
      },
      {
        id: 'pool_water',
        label: 'Pool & Water Features',
        icon: 'Waves',
        types: [
          { id: 'pool', label: 'Swimming Pool' },
          { id: 'pool_deck', label: 'Pool Deck' },
          { id: 'hot_tub_spa', label: 'Hot Tub / Spa' },
          { id: 'pond_fountain', label: 'Pond / Fountain' },
        ]
      },
      {
        id: 'auxiliary_structures',
        label: 'Auxiliary Structures',
        icon: 'Warehouse',
        types: [
          { id: 'detached_garage', label: 'Detached Garage' },
          { id: 'shed', label: 'Shed / Storage Building' },
          { id: 'guest_house', label: 'Guest House / ADU' },
          { id: 'barn', label: 'Barn / Outbuilding' },
          { id: 'greenhouse', label: 'Greenhouse' },
        ]
      }
    ]
  },
  {
    id: 'interior',
    label: 'Interior',
    icon: 'LayoutDashboard',
    categories: [
      {
        id: 'living_spaces',
        label: 'Living Spaces',
        icon: 'Sofa',
        types: [
          { id: 'living_room', label: 'Living Room', suggestedStyles: ['modern', 'traditional', 'minimalist', 'scandinavian'] },
          { id: 'family_room', label: 'Family Room / Den' },
          { id: 'great_room', label: 'Great Room' },
          { id: 'formal_living', label: 'Formal Living Room' },
          { id: 'sitting_room', label: 'Sitting Room / Parlor' },
          { id: 'sunroom', label: 'Sunroom / Florida Room' },
          { id: 'conservatory', label: 'Conservatory' },
        ]
      },
      {
        id: 'dining',
        label: 'Dining',
        icon: 'UtensilsCrossed',
        types: [
          { id: 'dining_room', label: 'Dining Room' },
          { id: 'formal_dining', label: 'Formal Dining Room' },
          { id: 'breakfast_nook', label: 'Breakfast Nook' },
          { id: 'eat_in_kitchen', label: 'Eat-in Kitchen Area' },
          { id: 'dining_alcove', label: 'Dining Alcove' },
        ]
      },
      {
        id: 'kitchen',
        label: 'Kitchen',
        icon: 'ChefHat',
        types: [
          { id: 'kitchen_galley', label: 'Kitchen - Galley' },
          { id: 'kitchen_l_shaped', label: 'Kitchen - L-Shaped' },
          { id: 'kitchen_u_shaped', label: 'Kitchen - U-Shaped' },
          { id: 'kitchen_island', label: 'Kitchen - Open with Island' },
          { id: 'kitchen_peninsula', label: 'Kitchen - Peninsula' },
          { id: 'kitchen_eat_in', label: 'Kitchen - Eat-in' },
          { id: 'butlers_pantry', label: "Butler's Pantry" },
          { id: 'kitchenette', label: 'Kitchenette' },
        ]
      },
      {
        id: 'bedrooms',
        label: 'Bedrooms',
        icon: 'Bed',
        types: [
          { id: 'primary_bedroom', label: 'Primary Bedroom' },
          { id: 'secondary_bedroom', label: 'Secondary Bedroom' },
          { id: 'guest_bedroom', label: 'Guest Bedroom' },
          { id: 'kids_bedroom', label: 'Kids Bedroom' },
          { id: 'nursery', label: 'Nursery' },
          { id: 'teen_bedroom', label: 'Teen Bedroom' },
          { id: 'guest_suite', label: 'Guest Suite' },
          { id: 'au_pair_suite', label: 'Au Pair / In-law Suite' },
        ]
      },
      {
        id: 'bathrooms',
        label: 'Bathrooms',
        icon: 'Bath',
        types: [
          { id: 'primary_bath_full', label: 'Primary Bathroom - Full' },
          { id: 'primary_bath_luxury', label: 'Primary Bathroom - Luxury/Spa' },
          { id: 'full_bath', label: 'Full Bathroom' },
          { id: 'three_quarter_bath', label: '3/4 Bathroom' },
          { id: 'half_bath', label: 'Half Bath / Powder Room' },
          { id: 'jack_and_jill', label: 'Jack and Jill Bathroom' },
          { id: 'ensuite', label: 'En-suite Bathroom' },
        ]
      },
      {
        id: 'work_spaces',
        label: 'Work Spaces',
        icon: 'Briefcase',
        types: [
          { id: 'home_office', label: 'Home Office' },
          { id: 'study', label: 'Study / Library' },
          { id: 'office_nook', label: 'Office Nook / Alcove' },
          { id: 'craft_room', label: 'Craft Room' },
          { id: 'workshop', label: 'Workshop' },
          { id: 'studio', label: 'Studio' },
        ]
      },
      {
        id: 'utility',
        label: 'Utility & Storage',
        icon: 'Archive',
        types: [
          { id: 'laundry_room', label: 'Laundry Room' },
          { id: 'laundry_closet', label: 'Laundry Closet' },
          { id: 'mudroom', label: 'Mudroom' },
          { id: 'utility_room', label: 'Utility Room' },
          { id: 'pantry', label: 'Pantry' },
          { id: 'walk_in_closet', label: 'Walk-in Closet' },
          { id: 'linen_closet', label: 'Linen Closet' },
          { id: 'storage_room', label: 'Storage Room' },
        ]
      },
      {
        id: 'entry_transition',
        label: 'Entry & Transition',
        icon: 'DoorClosed',
        types: [
          { id: 'foyer', label: 'Foyer / Entry Hall' },
          { id: 'hallway', label: 'Hallway' },
          { id: 'staircase', label: 'Staircase' },
          { id: 'landing', label: 'Landing' },
          { id: 'vestibule', label: 'Vestibule' },
          { id: 'gallery', label: 'Gallery / Corridor' },
        ]
      },
      {
        id: 'specialty_rooms',
        label: 'Specialty Rooms',
        icon: 'Star',
        types: [
          { id: 'home_gym', label: 'Home Gym' },
          { id: 'media_room', label: 'Media Room / Theater' },
          { id: 'game_room', label: 'Game Room' },
          { id: 'playroom', label: 'Playroom' },
          { id: 'music_room', label: 'Music Room' },
          { id: 'wine_cellar', label: 'Wine Cellar' },
          { id: 'sauna', label: 'Sauna' },
          { id: 'meditation_room', label: 'Meditation Room' },
        ]
      },
      {
        id: 'basement',
        label: 'Basement',
        icon: 'Layers',
        types: [
          { id: 'basement_finished', label: 'Basement - Finished' },
          { id: 'basement_unfinished', label: 'Basement - Unfinished' },
          { id: 'basement_partial', label: 'Basement - Partially Finished' },
          { id: 'basement_walkout', label: 'Basement - Walkout' },
          { id: 'cellar', label: 'Cellar' },
        ]
      },
      {
        id: 'attic',
        label: 'Attic & Upper',
        icon: 'Triangle',
        types: [
          { id: 'attic_finished', label: 'Attic - Finished' },
          { id: 'attic_unfinished', label: 'Attic - Unfinished' },
          { id: 'loft', label: 'Loft' },
          { id: 'bonus_room', label: 'Bonus Room' },
          { id: 'dormer_room', label: 'Dormer Room' },
        ]
      },
      {
        id: 'garage_interior',
        label: 'Garage Interior',
        icon: 'Car',
        types: [
          { id: 'garage_1car', label: '1-Car Garage' },
          { id: 'garage_2car', label: '2-Car Garage' },
          { id: 'garage_3car', label: '3+ Car Garage' },
          { id: 'garage_tandem', label: 'Tandem Garage' },
        ]
      }
    ]
  }
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a complete StructureSelection object from type ID (searches all domains/categories)
 * Can also be called with explicit domain and category for faster lookup
 */
export function getStructureSelection(typeId: string): StructureSelection | null;
export function getStructureSelection(
  domainId: 'interior' | 'exterior',
  categoryId: string,
  typeId: string
): StructureSelection | null;
export function getStructureSelection(
  domainIdOrTypeId: string,
  categoryId?: string,
  typeId?: string
): StructureSelection | null {
  // If called with just typeId, search for it
  if (!categoryId || !typeId) {
    const searchTypeId = domainIdOrTypeId;
    for (const domain of structureHierarchy) {
      for (const category of domain.categories) {
        const type = category.types.find(t => t.id === searchTypeId);
        if (type) {
          return {
            domain: domain.id,
            categoryId: category.id,
            typeId: type.id,
            domainLabel: domain.label,
            categoryLabel: category.label,
            typeLabel: type.label,
            fullPath: `${domain.label} › ${category.label} › ${type.label}`,
          };
        }
      }
    }
    return null;
  }

  // Called with explicit domain, category, type
  const domainId = domainIdOrTypeId as 'interior' | 'exterior';
  const domain = structureHierarchy.find(d => d.id === domainId);
  if (!domain) return null;

  const category = domain.categories.find(c => c.id === categoryId);
  if (!category) return null;

  const type = category.types.find(t => t.id === typeId);
  if (!type) return null;

  return {
    domain: domainId,
    categoryId,
    typeId,
    domainLabel: domain.label,
    categoryLabel: category.label,
    typeLabel: type.label,
    fullPath: `${domain.label} › ${category.label} › ${type.label}`,
  };
}

/**
 * Get suggested staging styles for a structure type
 */
export function getSuggestedStyles(typeId: string): string[] {
  for (const domain of structureHierarchy) {
    for (const category of domain.categories) {
      const type = category.types.find(t => t.id === typeId);
      if (type?.suggestedStyles) {
        return type.suggestedStyles;
      }
    }
  }
  // Default styles if none specified
  return ['modern', 'contemporary', 'traditional', 'minimalist'];
}

/**
 * Search structure types by query string
 */
export function searchStructureTypes(query: string): Array<{
  domain: StructureDomain;
  category: StructureCategory;
  type: StructureType;
}> {
  const results: Array<{
    domain: StructureDomain;
    category: StructureCategory;
    type: StructureType;
  }> = [];

  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return results;

  for (const domain of structureHierarchy) {
    for (const category of domain.categories) {
      for (const type of category.types) {
        if (
          type.label.toLowerCase().includes(lowerQuery) ||
          type.id.toLowerCase().includes(lowerQuery) ||
          category.label.toLowerCase().includes(lowerQuery)
        ) {
          results.push({ domain, category, type });
        }
      }
    }
  }

  return results;
}

/**
 * Get a domain by ID
 */
export function getDomain(domainId: 'interior' | 'exterior'): StructureDomain | undefined {
  return structureHierarchy.find(d => d.id === domainId);
}

/**
 * Get a category by domain and category ID
 */
export function getCategory(
  domainId: 'interior' | 'exterior',
  categoryId: string
): StructureCategory | undefined {
  const domain = getDomain(domainId);
  return domain?.categories.find(c => c.id === categoryId);
}

/**
 * Get a structure type by its ID (searches all domains/categories)
 */
export function getStructureType(typeId: string): StructureType | undefined {
  for (const domain of structureHierarchy) {
    for (const category of domain.categories) {
      const type = category.types.find(t => t.id === typeId);
      if (type) return type;
    }
  }
  return undefined;
}

/**
 * Get all structure type labels as a flat map (for prompt generation)
 */
export function getAllStructureTypeLabels(): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const domain of structureHierarchy) {
    for (const category of domain.categories) {
      for (const type of category.types) {
        labels[type.id] = type.label;
      }
    }
  }
  return labels;
}

/**
 * Get a human-readable label for a structure type
 * Falls back to title-casing the type ID if not found
 */
export function getStructureLabel(structureType: string): string {
  const type = getStructureType(structureType);
  if (type) {
    return type.label;
  }
  // Fallback: convert snake_case to Title Case
  return structureType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format structure type for use in filenames (snake_case to kebab-case)
 */
export function formatStructureTypeForFilename(structureType: string): string {
  return structureType.replace(/_/g, '-');
}


/**
 * Get the default structure selection (Living Room)
 */
export function getDefaultStructureSelection(): StructureSelection {
  return getStructureSelection('interior', 'living_spaces', 'living_room')!;
}

// ============================================================================
// Style Recommendation Helpers
// ============================================================================

/**
 * Default style recommendations by category
 * Used as fallback when database applicability data is not available
 */
const DEFAULT_STYLE_RECOMMENDATIONS: Record<string, string[]> = {
  // Interior categories
  living_spaces: ['modern', 'scandinavian', 'midcentury', 'traditional', 'contemporary'],
  dining: ['traditional', 'modern', 'scandinavian', 'farmhouse', 'contemporary'],
  kitchen: ['modern', 'farmhouse', 'contemporary', 'scandinavian', 'traditional'],
  bedrooms: ['scandinavian', 'modern', 'traditional', 'coastal', 'minimalist'],
  bathrooms: ['modern', 'minimalist', 'luxury', 'coastal', 'scandinavian'],
  work_spaces: ['modern', 'minimalist', 'industrial', 'scandinavian', 'midcentury'],
  utility: ['modern', 'farmhouse', 'minimalist', 'scandinavian'],
  entry_transition: ['minimalist', 'modern', 'traditional', 'scandinavian'],
  specialty_rooms: ['modern', 'industrial', 'midcentury', 'luxury', 'contemporary'],
  basement: ['modern', 'industrial', 'contemporary', 'minimalist'],
  attic: ['modern', 'scandinavian', 'industrial', 'minimalist'],
  garage_interior: ['industrial', 'modern', 'minimalist'],
  // Exterior categories
  facade: ['modern', 'traditional', 'farmhouse', 'contemporary'],
  outdoor_spaces: ['coastal', 'farmhouse', 'modern', 'contemporary'],
  entry_access: ['farmhouse', 'traditional', 'modern', 'coastal'],
  outdoor_living: ['coastal', 'modern', 'farmhouse', 'contemporary'],
  parking_garage: ['modern', 'contemporary'],
  pool_water: ['coastal', 'modern', 'luxury', 'contemporary'],
  auxiliary_structures: ['farmhouse', 'modern', 'traditional'],
};

/**
 * Get default recommended styles for a structure selection
 * This is a synchronous fallback when database query is not available
 */
export function getDefaultStylesForStructure(selection: StructureSelection): string[] {
  // First try category-specific recommendations
  if (DEFAULT_STYLE_RECOMMENDATIONS[selection.categoryId]) {
    return DEFAULT_STYLE_RECOMMENDATIONS[selection.categoryId];
  }

  // Fallback based on domain
  if (selection.domain === 'exterior') {
    return ['modern', 'coastal', 'farmhouse', 'contemporary', 'traditional'];
  }

  // Default interior styles
  return ['modern', 'contemporary', 'scandinavian', 'traditional', 'minimalist'];
}

/**
 * Check if a style is appropriate for exterior spaces
 */
export function isExteriorAppropriateStyle(styleSlug: string): boolean {
  const exteriorStyles = new Set([
    'modern',
    'contemporary',
    'traditional',
    'farmhouse',
    'coastal',
    'luxury',
    'minimalist',
  ]);
  return exteriorStyles.has(styleSlug);
}

/**
 * Check if a style is appropriate for interior spaces
 */
export function isInteriorAppropriateStyle(styleSlug: string): boolean {
  // Most styles work for interior
  const interiorStyles = new Set([
    'modern',
    'contemporary',
    'traditional',
    'minimalist',
    'industrial',
    'scandinavian',
    'midcentury',
    'luxury',
    'farmhouse',
    'coastal',
  ]);
  return interiorStyles.has(styleSlug);
}

/**
 * Get style recommendations for a structure type based on its characteristics
 */
export function getStructureStyleContext(selection: StructureSelection): {
  primaryStyles: string[];
  avoidStyles: string[];
  styleNotes: string;
} {
  const { domain, categoryId, typeId } = selection;

  // Special cases for specific structure types
  if (typeId === 'primary_bath_luxury') {
    return {
      primaryStyles: ['luxury', 'modern', 'minimalist'],
      avoidStyles: ['industrial', 'farmhouse'],
      styleNotes: 'Luxury bathrooms benefit from high-end, spa-like styling',
    };
  }

  if (typeId === 'nursery') {
    return {
      primaryStyles: ['scandinavian', 'modern', 'minimalist'],
      avoidStyles: ['industrial', 'luxury'],
      styleNotes: 'Nurseries should feel warm, safe, and not overly cluttered',
    };
  }

  if (typeId === 'wine_cellar') {
    return {
      primaryStyles: ['luxury', 'traditional', 'industrial'],
      avoidStyles: ['coastal', 'scandinavian'],
      styleNotes: 'Wine cellars suit dramatic, sophisticated styling',
    };
  }

  if (typeId === 'barn') {
    return {
      primaryStyles: ['farmhouse', 'traditional'],
      avoidStyles: ['modern', 'minimalist', 'scandinavian'],
      styleNotes: 'Barns should maintain rustic authenticity',
    };
  }

  if (categoryId === 'pool_water') {
    return {
      primaryStyles: ['coastal', 'modern', 'luxury'],
      avoidStyles: ['industrial', 'farmhouse'],
      styleNotes: 'Pool areas benefit from resort-like, relaxed styling',
    };
  }

  if (categoryId === 'garage_interior') {
    return {
      primaryStyles: ['industrial', 'modern', 'minimalist'],
      avoidStyles: ['traditional', 'farmhouse', 'coastal'],
      styleNotes: 'Garages suit clean, functional industrial aesthetics',
    };
  }

  // Default by domain
  if (domain === 'exterior') {
    return {
      primaryStyles: ['modern', 'coastal', 'farmhouse', 'contemporary'],
      avoidStyles: ['industrial'],
      styleNotes: 'Exterior spaces should complement the architectural style',
    };
  }

  // Default interior
  return {
    primaryStyles: getDefaultStylesForStructure(selection),
    avoidStyles: [],
    styleNotes: 'Style should match the room\'s function and desired atmosphere',
  };
}

// ============================================================================
// Database Fetching Functions
// ============================================================================

import { supabase, type Database } from './supabase';
import { logger } from './logger';

type DbStructureDomain = Database['public']['Tables']['structure_domains']['Row'];
type DbStructureCategory = Database['public']['Tables']['structure_categories']['Row'];
type DbStructureType = Database['public']['Tables']['structure_types']['Row'];
type DbStagingDefaults = Database['public']['Tables']['structure_staging_defaults']['Row'];

/**
 * Fetch structure hierarchy from database
 * Returns the hardcoded hierarchy as fallback if database is unavailable
 */
export async function fetchStructureHierarchy(): Promise<StructureHierarchy> {
  try {
    // Fetch all active domains
    const { data: domains, error: domainsError } = await supabase
      .from('structure_domains')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (domainsError || !domains || domains.length === 0) {
      logger.warn('Using fallback structure hierarchy (domains fetch failed)');
      return structureHierarchy;
    }

    // Fetch all active categories
    const { data: categories, error: categoriesError } = await supabase
      .from('structure_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (categoriesError || !categories) {
      logger.warn('Using fallback structure hierarchy (categories fetch failed)');
      return structureHierarchy;
    }

    // Fetch all active types
    const { data: types, error: typesError } = await supabase
      .from('structure_types')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (typesError || !types) {
      logger.warn('Using fallback structure hierarchy (types fetch failed)');
      return structureHierarchy;
    }

    // Build the hierarchy
    return domains.map((domain: DbStructureDomain) => ({
      id: domain.slug as 'interior' | 'exterior',
      label: domain.name,
      icon: domain.icon || undefined,
      categories: categories
        .filter((cat: DbStructureCategory) => cat.domain_id === domain.id)
        .map((cat: DbStructureCategory) => ({
          id: cat.slug,
          label: cat.name,
          icon: cat.icon || undefined,
          types: types
            .filter((t: DbStructureType) => t.category_id === cat.id)
            .map((t: DbStructureType) => ({
              id: t.slug,
              label: t.name,
              description: t.description || undefined,
              suggestedStyles: [], // Will be populated from staging_defaults
            })),
        })),
    }));
  } catch (error) {
    logger.error('Error fetching structure hierarchy:', error);
    return structureHierarchy;
  }
}

/**
 * Fetch structure type by slug from database
 */
export async function fetchStructureTypeBySlug(slug: string): Promise<{
  type: DbStructureType;
  category: DbStructureCategory;
  domain: DbStructureDomain;
} | null> {
  try {
    const { data: type, error: typeError } = await supabase
      .from('structure_types')
      .select(`
        *,
        structure_categories!inner (
          *,
          structure_domains!inner (*)
        )
      `)
      .eq('slug', slug)
      .single();

    if (typeError || !type) {
      return null;
    }

    const category = (type as any).structure_categories as DbStructureCategory;
    const domain = (category as any).structure_domains as DbStructureDomain;

    return { type, category, domain };
  } catch (error) {
    logger.error('Error fetching structure type by slug:', error);
    return null;
  }
}

/**
 * Fetch staging defaults for a structure type
 */
export async function fetchStagingDefaults(structureTypeSlug: string): Promise<DbStagingDefaults | null> {
  try {
    // First get the structure type ID
    const { data: type, error: typeError } = await supabase
      .from('structure_types')
      .select('id')
      .eq('slug', structureTypeSlug)
      .single();

    if (typeError || !type) {
      return null;
    }

    // Then get the staging defaults
    const { data: defaults, error: defaultsError } = await supabase
      .from('structure_staging_defaults')
      .select('*')
      .eq('structure_type_id', type.id)
      .eq('is_active', true)
      .single();

    if (defaultsError || !defaults) {
      return null;
    }

    return defaults;
  } catch (error) {
    logger.error('Error fetching staging defaults:', error);
    return null;
  }
}

/**
 * Get recommended styles from staging defaults (database) or fallback
 */
export async function getRecommendedStylesForStructure(
  selection: StructureSelection
): Promise<string[]> {
  // Try to get from database first
  const defaults = await fetchStagingDefaults(selection.typeId);
  if (defaults && defaults.recommended_styles.length > 0) {
    return defaults.recommended_styles;
  }

  // Fallback to hardcoded recommendations
  return getDefaultStylesForStructure(selection);
}

/**
 * Get incompatible styles from staging defaults (database) or fallback
 */
export async function getIncompatibleStylesForStructure(
  selection: StructureSelection
): Promise<string[]> {
  const defaults = await fetchStagingDefaults(selection.typeId);
  if (defaults && defaults.incompatible_styles) {
    return defaults.incompatible_styles;
  }

  // Fallback to hardcoded context
  const context = getStructureStyleContext(selection);
  return context.avoidStyles;
}

/**
 * Convert database StructureSelection to full StructureSelection with labels
 */
export async function buildStructureSelectionFromDb(
  domainSlug: string,
  categorySlug: string,
  typeSlug: string
): Promise<StructureSelection | null> {
  // First try hardcoded hierarchy (faster)
  const fromHardcoded = getStructureSelection(
    domainSlug as 'interior' | 'exterior',
    categorySlug,
    typeSlug
  );
  
  if (fromHardcoded) {
    return fromHardcoded;
  }

  // Try database if not in hardcoded
  const dbType = await fetchStructureTypeBySlug(typeSlug);
  if (!dbType) {
    return null;
  }

  const { type, category, domain } = dbType;

  return {
    domain: domain.slug as 'interior' | 'exterior',
    categoryId: category.slug,
    typeId: type.slug,
    domainLabel: domain.name,
    categoryLabel: category.name,
    typeLabel: type.name,
    fullPath: `${domain.name} › ${category.name} › ${type.name}`,
  };
}
